import { useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useIdentity } from '@zenbusiness/application-commons-identity';
import Button from '@zenbusiness/common-components/Button';
import Text from '@zenbusiness/common-components/Text';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';

import SharedAsset from '#client/components/SharedAsset';
import { useBreakpoints } from '#client/hooks/useBreakpoints';
import useFormationTimelineQuery from '#client/hooks/useFormationTimelineQuery';
import manifest from '#client/manifest';
import { isFormationComplete } from '#client/utils/businessEntity';
import { JS_EARLIEST_DATE } from '#client/utils/date';
import { buildCartUrl } from '#client/utils/urls';

import { useMerchantAccountHolder, useOrders } from './queries';

const OnboardingMessage = styled(Box)(({ theme, isClosable, isPending }) => ({
  marginBottom: '40px',
  padding: '16px',
  backgroundColor: isPending ? theme.palette.wisdom10.main : theme.palette.growth10.main,
  borderRadius: '24px',
  color: theme.palette.focus90.main,
  display: 'flex',
  flexDirection: isClosable ? 'row' : 'column',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '16px',
  '& img': {
    paddingTop: '4px'
  },
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: '24px',
    padding: '24px 32px',
    marginBottom: '64px',
    '& img': {
      paddingTop: '0'
    }
  }
}));

export const {
  merchantOnboardingRequireFormationComplete,
  invoiceCTAShowAfterFormationDate
} = manifest?.fintech ?? {
  merchantOnboardingRequireFormationComplete: true,
  invoiceCTAShowAfterFormationDate: null
};

export const isComplete = (status) => ['complete', 'active', 'canceled'].includes(status);

export const isFormationTaskType = (type) => type?.includes('pro')
  || type?.includes('premium')
  || type?.startsWith('starter');

export const isRequiredTaskType = (type) => type === 'ein_creation'
  || isFormationTaskType(type);

export const getIsPendingOrderTask = (orders) => orders
  ?.reduce((hasPendingEINTask, order) => hasPendingEINTask
  || order?.orderTasksLegacy?.reduce(
    (hasEINTask, orderTask) => hasEINTask
    || (isRequiredTaskType(orderTask.orderTaskType) && !isComplete(orderTask.orderTaskStatus)),
    false
  ), false);

export const getFormationDateThreshold = () => new Date(
  manifest?.fintech?.invoiceCTAShowAfterFormationDate || JS_EARLIEST_DATE
);

export const getIsAfterDate = (orders, formationDateThreshold) => orders
  ?.reduce((hasAfterDateTask, order) => hasAfterDateTask
  || order?.orderTasksLegacy?.reduce(
    (hasValidTask, orderTask) => hasValidTask
    || (isFormationTaskType(orderTask.orderTaskType)
    && new Date(orderTask.createdDatetime) > new Date(formationDateThreshold)),
    false
  ), false);

export const isMerchantAccountPending = (businessEntityUuid) => (merchantAccount) => merchantAccount.businessEntityUuid === businessEntityUuid && merchantAccount.providerStatus === 'pending';

export const isNoMerchantApplication = (merchantAccountHolder) => !merchantAccountHolder || (!!merchantAccountHolder && !['pending_kyc', 'complete'].includes(merchantAccountHolder?.onboardingStatus));

export const isMerchantApplicationPending = (merchantAccountHolder, businessEntityUuid) => merchantAccountHolder?.onboardingStatus === 'pending_kyc'
  || (merchantAccountHolder?.onboardingStatus === 'complete'
      && !!merchantAccountHolder?.merchantAccounts
        ?.find(isMerchantAccountPending(businessEntityUuid)));

export const isOnboardingComplete = (merchantAccountHolder) => !!merchantAccountHolder && merchantAccountHolder?.onboardingStatus === 'complete';

export const isNoTimeline = (error) => !!error?.graphQLErrors?.find((e) => e.extensions?.errorCode === 'TIMELINE_NOT_FOUND');

export const useFormationTimelineDataEvents = (businessEntityUuid, orders) => {
  const orderTasks = orders.reduce((tasks, order) => tasks.concat(order.orderTasksLegacy), []);
  const { error, data, loading } = useFormationTimelineQuery(businessEntityUuid, orderTasks);
  if (!data || isNoTimeline(error)) {
    return { events: null, loading: false };
  }
  if (error) throw error;
  return { events: data?.events, loading };
};

export default function MerchantOnboardingMessage() {
  const { accountUuid } = useIdentity();

  const { businessEntityUuid } = useBusinessEntity();

  const { orders } = useOrders(businessEntityUuid);
  const pendingOrderTask = getIsPendingOrderTask(orders);

  // MM not sure this is still needed
  // const formationAfterDate = getIsAfterDate(orders, getFormationDateThreshold());

  const { events } = useFormationTimelineDataEvents(businessEntityUuid, orders);
  const isFormationFinished = isFormationComplete(events);

  const isFormationAndEINComplete = !merchantOnboardingRequireFormationComplete
                                    || (
                                      isFormationFinished
                                      && !pendingOrderTask
                                    );

  const merchantAccountHolder = useMerchantAccountHolder(accountUuid);
  const noMerchantApplication = isNoMerchantApplication(merchantAccountHolder);
  const pendingMerchantApplication = !noMerchantApplication
    && isMerchantApplicationPending(merchantAccountHolder, businessEntityUuid);

  const onBoardingComplete = isOnboardingComplete(merchantAccountHolder);
  const { sizes: { isMobile } } = useBreakpoints();
  const isStripeFlow = useExperimentFlag('enable-stripe-flow', false);
  const [isClosed, setIsClosed] = useState(false);

  if (onBoardingComplete) {
    return (
      <>
        {!isClosed && (
          <OnboardingMessage isClosable>
            <Box display="flex" gap="24px" alignItems="center">
              {!isMobile && (
                <SharedAsset
                  src="/s2/vector/online-payments.svg"
                  alt="Online payments"
                  width={64}
                  height={64}
                />
              )}
              <div>
                <Text size="xl" bold>You&apos;re ready to get paid online!</Text>
                <Text size="md">Set up your invoice template to save time.</Text>
              </div>
            </Box>
            <IconButton
              onClick={() => setIsClosed(true)}
            >
              <SharedAsset
                src="/s2/icon/e-remove-outline.svg"
                width={24}
                height={24}
              />
            </IconButton>
          </OnboardingMessage>
        )}
      </>
    );
  }
  if (!isFormationAndEINComplete) {
    return (
      <OnboardingMessage>
        <Box>
          <Text size="xl" bold>You&apos;ll get an email when your business formation is complete.</Text>
          <Text size="md">Then you can set up online payments here.</Text>
        </Box>
      </OnboardingMessage>
    );
  }
  if (isFormationAndEINComplete && noMerchantApplication) {
    return (
      <OnboardingMessage>
        <Box display="flex" gap="24px" alignItems="center">
          {!isMobile && (
            <SharedAsset
              src="/s2/vector/online-payments.svg"
              alt="Online payments"
              width={64}
              height={64}
            />
          )}
          <div>
            <Text size="xl" bold>Finish setting up online payments</Text>
            <Text size="md">
              Let&apos;s get you ready to get paid!
              {isStripeFlow && ' Connect with Stripe to get started.'}
            </Text>
          </div>
        </Box>
        <Button
          href={buildCartUrl('zb_money_invoice_setup', { businessEntityUuid })}
          sx={{ width: { sm: 'fit-content' } }}
        >
          Set up payments
        </Button>
      </OnboardingMessage>
    );
  }
  if (isFormationAndEINComplete && pendingMerchantApplication) {
    return (
      <OnboardingMessage isPending>
        <Box display="flex" gap="24px" alignItems="center">
          {!isMobile && (
            <SharedAsset
              src="/icon/product/banking-resolution-key3-256.svg"
              alt="Online payments"
              width={64}
              height={64}
            />
          )}
          <div>
            <Text size="xl" bold>Your payments account is pending approval</Text>
            <Text size="md">We&apos;re still processing your application and will let you know when it&apos;s ready.</Text>
          </div>
        </Box>
      </OnboardingMessage>
    );
  }
  return <></>;
}
